import { memo, useCallback } from 'react';
import classnames from 'classnames';

import PopperTooltip from 'shared/ui/Popper/PopperTooltip';
import { attachmentTypeCodes } from 'shared/constants';
import { BaseAttachmentDto } from '@eva/types/dto';

// eslint-disable-next-line import/no-cycle
import {
  isCandidateFile,
  isPrivateFile,
  fileExpired,
  formatCreated,
  getFileId,
  getFileType,
  getFileLabel,
  getFileTypeCodeOfFile,
  getFileTypeNameOfFile,
} from '../functions';

const cursorPointerStyle = {
  cursor: 'pointer',
};

interface FileLabelProps<F> {
  file: F;
  dateFormat?: string;
  inline?: boolean;
  onFileSelected?: (file: F) => void;
}

const FileLabel = <F extends BaseAttachmentDto>({ file, inline, dateFormat, onFileSelected }: FileLabelProps<F>) => {
  let disabled = false;

  if (isPrivateFile(file)) {
    disabled = file.privateFileType?.disabled;
  }

  if (isCandidateFile(file)) {
    disabled = file.attachmentType?.disabled;
  }

  const expired = fileExpired(file);

  let badgeType = '';

  if (file.verified && !expired) {
    badgeType = 'bg-success';
  } else if (expired) {
    badgeType = 'bg-warning';
  }

  const classNames = classnames({
    'files-list__file-label': true,
    'files-list__file-label_inline': true,
    'files-list__file-label_disabled': disabled,
  });

  const onClick = useCallback(() => {
    if (typeof onFileSelected === 'function') {
      onFileSelected(file);
    }
  }, [file, onFileSelected]);

  const title = getFileLabel(file);

  const containerRole = typeof onFileSelected === 'function' ? 'button' : 'presentation';

  const titleStyle = typeof onFileSelected === 'function' ? cursorPointerStyle : undefined;

  const titleClassNames = inline ? '' : 'margin-left file-title';

  const tooltip = inline ? null : (
    <PopperTooltip
      placement="top"
      overlay={
        <span>
          {translate('Uploaded')}: {formatCreated(file, dateFormat)}
        </span>
      }
    >
      <i className="lnr lnr-history text-muted" />
    </PopperTooltip>
  );

  return (
    <div key={getFileId(file)} role={containerRole} className={classNames} onClick={onClick}>
      {getFileType(file) != null && (
        <span className={`badge ${badgeType} pull-right`}>
          {file.verified && (
            <i
              className="fa fa-check"
              style={{
                marginRight: '5px',
              }}
            />
          )}
          {expired && (
            <i
              className="fa fa-clock-o"
              style={{
                marginRight: '5px',
              }}
            />
          )}
          {getFileTypeCodeOfFile(file) !== attachmentTypeCodes.noType ? (
            <span title={getFileTypeNameOfFile(file)}>{getFileTypeNameOfFile(file)}</span>
          ) : null}
        </span>
      )}

      <div className="files-list__file-left-part">
        {tooltip}
        <div className={titleClassNames}>
          <span className="text-dark-blue" style={titleStyle} title={title}>
            {title}
          </span>
        </div>
      </div>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(FileLabel);
